import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

// ---------------------------------------------------------------------------
// The context contract (design D): the URL parameters every page understands,
// so a page opened from an alert arrives with the alert's district, window,
// status and meter already applied - and can be bookmarked, shared or
// reloaded as it is. The URL is the source of truth.
//
//   district   GPMD             header district switcher -> every page
//   from / to  2026-11-01       alert window, range control -> every page
//   range      1M · 3M · YTD    preset buttons -> Water Usage, Analytics
//   status     leak · silent    Alert Center row -> Leak Flow, Backflow, No Recent Flow
//   meter      126171           list row, map pin -> Leak Flow, Anomaly, Water Usage
//   account    [account ID]     meter -> account lookup -> Water Usage, Reports
//   plant      west-plant       plant filter -> Water Usage, Capacity Planning
//   mark       2026-10-25..31   a week to mark -> Water Usage chart band
//   system     non_potable      Average Accountability -> Flow Analytics
//   view       accountability   Flow Analytics' sub-tab (accountability · intake · supply · demand)
//   category   non-potable-wp1  Anomaly category -> Analytics, Water Usage
//   cluster    e-sam-houston    Mizu AI Hotspots -> Leak Flow
//   src        dt.alert.leak    entry point -> source chip and Back button
//
// `meters` (a comma list, MeterFilterBanner) predates the contract and keeps
// its job: a set of meters a Mizu AI diagnosis row pointed at.
// ---------------------------------------------------------------------------

export const CONTEXT_KEYS = ["district", "from", "to", "range", "status", "meter", "account", "plant", "mark", "category", "cluster", "system", "view", "src"];

// The Modules menu's groups, in its order.
export const GROUPS = [
  { id: "monitor", label: "Monitor" },
  { id: "analyze", label: "Analyze" },
  { id: "reports", label: "Reports & Alerts" },
  { id: "admin", label: "Administration" },
];
export const groupLabel = (id) => GROUPS.find((g) => g.id === id)?.label || "Modules";

const userType = () => {
  try {
    return JSON.parse(localStorage.getItem("MCData") || "{}")?.type;
  } catch {
    return undefined;
  }
};

// Every legacy page, by key: its name, Modules group, where it lives, and how
// to tell it is the page on screen (`match`, given pathname and query).
// `count` names the Alert Center count shown beside it in the menu.
export const PAGES = {
  dashboard: { label: "Dashboard", group: "monitor", to: "/dashboard", match: (p) => p === "/dashboard" },
  "water-usage": { label: "Water Usage", group: "monitor", to: "/waters", match: (p) => ["/waters", "/wateragg", "/watermeterreadings"].includes(p) },
  "system-health": { label: "System Health", group: "monitor", to: "/health", match: (p) => p === "/health" },
  "flow-analytics": { label: "Flow Analytics", group: "analyze", to: "/totalflow?tab=flow", match: (p, q) => p === "/totalflow" && (q.get("tab") || "flow") === "flow" },
  predictive: { label: "Predictive Analytics", group: "analyze", to: "/totalflow?tab=predictive", match: (p, q) => p === "/totalflow" && q.get("tab") === "predictive" },
  anomaly: { label: "Anomaly Detection", group: "analyze", to: "/totalflow?tab=anomaly", match: (p, q) => p === "/totalflow" && q.get("tab") === "anomaly", count: "anomaly" },
  capacity: { label: "Capacity Planning", group: "analyze", to: "/capacity-planning", match: (p) => p === "/capacity-planning" },
  "leak-flow": { label: "Leak Flow", group: "reports", to: "/leak-flow-category", match: (p) => p === "/leak-flow-category", count: "leak" },
  backflow: { label: "Backflow", group: "reports", to: "/flow-health?status=backflow", match: (p, q) => p === "/flow-health" && flowStatus(q) === "backflow", count: "backflow" },
  "no-recent-flow": { label: "No Recent Flow", group: "reports", to: "/flow-health?status=silent", match: (p, q) => p === "/flow-health" && flowStatus(q) === "silent", count: "noflow" },
  "flow-health": { label: "Flow Health", group: "reports", to: "/flow-health?status=leak", match: (p, q) => p === "/flow-health" && flowStatus(q) === "leak", hidden: true },
  reports: { label: "Reports", group: "reports", to: "/reportsandalerts", match: (p) => p === "/reportsandalerts" || p === "/alert" },
  outliers: { label: "Data Outliers", group: "reports", to: "/reportsandalerts", state: { activeMainTab: "outliers" }, match: () => false },
  "meter-detail": { label: "Meter detail", group: "reports", to: "/meter-leak-details", match: (p) => p === "/meter-leak-details", hidden: true },
  profile: { label: "Profile", group: null, to: "/profile", match: (p) => p === "/profile", hidden: true },
};

// Administration: Listuser's tabs, as the old menu listed them. A user (type
// U) cannot open the first four.
const ADMIN_TABS = [
  { id: "settings", label: "Settings", restricted: true },
  { id: "users", label: "Users & roles", restricted: true },
  { id: "accounts", label: "Accounts" },
  { id: "meter-management", label: "Meters & accounts", restricted: true },
  { id: "meter-history", label: "Meter History" },
  { id: "leak-rate", label: "Field Logs", restricted: true },
  { id: "leak-rate-water", label: "Rate Schedule" },
];
ADMIN_TABS.forEach((tab) => {
  PAGES[`admin-${tab.id}`] = {
    label: tab.label,
    group: "admin",
    to: `/settingsuser?tab=${tab.id}`,
    restricted: tab.restricted,
    match: (p, q) => p === "/settingsuser" && (q.get("tab") || "settings") === tab.id,
  };
});

// The pages the menu lists for this user, by group.
export function menuGroups() {
  const restricted = userType() === "U";
  return GROUPS.map((group) => ({
    ...group,
    pages: Object.entries(PAGES)
      .filter(([, page]) => page.group === group.id && !page.hidden && !(restricted && page.restricted))
      .map(([key, page]) => ({ key, ...page })),
  }));
}

// Flow Health's view: the contract's status (leak · backflow · silent), or the
// older ?tab= names.
export function flowStatus(query) {
  const status = query.get("status") || query.get("tab") || "";
  if (status === "backflow") return "backflow";
  if (status === "silent" || status === "no_recent_flow") return "silent";
  if (status === "leak") return "leak";
  return null;
}

// The page on screen, if it is one of PAGES.
export function pageAt(pathname, search) {
  const query = new URLSearchParams(search);
  const path = pathname.toLowerCase();
  const found = Object.entries(PAGES).find(([, page]) => page.match(path, query));
  return found ? { key: found[0], ...found[1] } : null;
}

// ── Sources: where a page was opened from (`src`) ──────────────────────────
// `chip` is the source chip ("From Alert Center · Leak"); Back names `back`
// and goes to `to`. Digital Twin restores the map view the user left
// (restore=1, legacy.html keeps it in the session).
const TWIN = "/new-dashboard?tab=twin&restore=1";
const MIZU = "/new-dashboard?tab=alerts";
export const SOURCES = {
  "dt.alert.leak": { chip: "Alert Center · Leak", back: "Digital Twin", to: TWIN },
  "dt.alert.anomaly": { chip: "Alert Center · Anomaly", back: "Digital Twin", to: TWIN },
  "dt.alert.backflow": { chip: "Alert Center · Backflow", back: "Digital Twin", to: TWIN },
  "dt.alert.silent": { chip: "Alert Center · No Recent Flow", back: "Digital Twin", to: TWIN },
  "dt.alert.health": { chip: "Alert Center · System Health", back: "Digital Twin", to: TWIN },
  "dt.acct": { chip: "Alert Center · Accountability", back: "Digital Twin", to: TWIN },
  "dt.timeline": { chip: "Timeline", back: "Digital Twin", to: TWIN },
  "dt.meter": { chip: "Digital Twin · Meter", back: "Digital Twin", to: TWIN },
  "dt.hotspot": { chip: "Mizu AI · Hotspots", back: "Digital Twin", to: TWIN },
  "mizu.status": { chip: "Mizu AI · Live Status", back: "Mizu AI", to: MIZU },
  "mizu.hotspot": { chip: "Mizu AI · Hotspots", back: "Mizu AI", to: MIZU },
  "mizu.diagnosis": { chip: "Mizu AI · Diagnosis", back: "Mizu AI", to: MIZU },
  "mizu.card": { chip: "Mizu AI · Alert card", back: "Mizu AI", to: MIZU },
  // The classic Digital Twin page (/schematics), which opens Water Usage
  // with from=digitalTwin.
  "page.schematics": { chip: "Digital Twin (classic)", back: "Digital Twin", to: "/schematics" },
};

// A source: one of SOURCES, or another page ("page.leak-flow") - Back then
// returns to it, through the history when it is the page before - or MizuIQ,
// the chat that opened the page ("chat", or "chat.leak-flow" when asked on
// that page): Back returns to the page it was asked on.
export function sourceOf(src) {
  if (!src) return null;
  if (SOURCES[src]) return { key: src, ...SOURCES[src] };
  if (src === "chat" || src.startsWith("chat.")) {
    const asked = Object.prototype.hasOwnProperty.call(PAGES, src.slice(5)) ? PAGES[src.slice(5)] : null;
    return { key: src, chip: "MizuIQ", back: asked ? asked.label : "Back", to: asked ? asked.to : "/new-dashboard?tab=alerts", page: src.slice(5) || "chat" };
  }
  const pageKey = src.startsWith("page.") ? src.slice(5) : null;
  const page = pageKey && PAGES[pageKey];
  if (page) return { key: src, chip: page.label, back: page.label, to: page.to, page: pageKey };
  return null;
}

// A URL with context: `to` (a path, maybe with a query) plus params; empty
// values are left out.
export function withContext(to, params = {}) {
  const [path, query = ""] = to.split("?");
  const search = new URLSearchParams(query);
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") search.delete(key);
    else search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `${path}?${text}` : path;
}

// The context in the URL, and ways to change it. `set` merges (null drops a
// key) and replaces the history entry, so filtering does not pile up Back
// steps; `drop` removes keys.
export function useUrlContext() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ctx = useMemo(() => Object.fromEntries(CONTEXT_KEYS.map((key) => [key, params.get(key) || null])), [params]);
  const set = useCallback(
    (changes) => {
      const next = new URLSearchParams(window.location.search);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      const text = next.toString();
      navigate({ pathname: location.pathname, search: text ? `?${text}` : "" }, { replace: true, state: location.state });
    },
    [navigate, location.pathname, location.state],
  );
  const drop = useCallback((...keys) => set(Object.fromEntries(keys.map((key) => [key, null]))), [set]);
  return { ctx, params, set, drop };
}

// ── Recents (the Modules menu's "Recent" row) ──────────────────────────────
const RECENT_KEY = "mw2.recent";
export function readRecents() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}
export function rememberRecent(label, to) {
  try {
    const list = readRecents().filter((item) => item.label !== label);
    list.unshift({ label, to });
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 4)));
  } catch {
    /* private mode: no recents */
  }
}

// ── Alert counts for the menu ──────────────────────────────────────────────
// The dashboard's counts (alertCounts.js), kept for the session so the
// Modules menu can show them on every page - for the district they were
// counted in, while they are fresh.
const COUNTS_KEY = "mw2.alertCounts";
const COUNTS_FRESH_MS = 30 * 60000;
export function storeAlertCounts(district, counts) {
  try {
    const value = {};
    ["leak", "anomaly", "backflow", "noflow"].forEach((key) => {
      if (counts?.[key]?.count != null) value[key] = counts[key].count;
    });
    sessionStorage.setItem(COUNTS_KEY, JSON.stringify({ district, at: Date.now(), counts: value }));
  } catch {
    /* no storage */
  }
}
export function readAlertCounts(district) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(COUNTS_KEY) || "null");
    if (!saved || saved.district !== district || Date.now() - saved.at > COUNTS_FRESH_MS) return {};
    return saved.counts || {};
  } catch {
    return {};
  }
}

// ── Formatting shared by the pages ─────────────────────────────────────────
export const fmtInt = (n) => (n == null || !Number.isFinite(Number(n)) ? "--" : Math.round(Number(n)).toLocaleString("en-US"));
export const fmtMoney = (n) => (n == null || !Number.isFinite(Number(n)) ? "--" : `$${Math.floor(Number(n)).toLocaleString("en-US")}`);
// Parse "1,234", "$1,234.56", "12 K Gallons", "3 kgal" -> a number (gallons
// for volumes).
export function toNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = String(value).trim().toLowerCase().replace(/,/g, "");
  const matched = text.match(/-?\d+(\.\d+)?/);
  if (!matched) return null;
  let n = Number(matched[0]);
  if (/k\s*gal/.test(text)) n *= 1000;
  else if (/m\s*gal/.test(text)) n *= 1000000;
  return Number.isFinite(n) ? n : null;
}
// Gallons as kgal with one decimal under 100, whole above.
export const fmtKgal = (gallons) => {
  if (gallons == null || !Number.isFinite(gallons)) return "--";
  const k = gallons / 1000;
  return Math.abs(k) >= 100 ? Math.round(k).toLocaleString("en-US") : k.toLocaleString("en-US", { maximumFractionDigits: 1 });
};
